import {useContext, useMemo} from 'react';
import {PlayerStoreContext} from '@common/player/player-context';
import {MediaItem} from '@common/player/media-item';
import {getUserRolesAccess} from '@common/auth/user-roles-access';
import {Track} from '@app/web-player/tracks/track';
import {trackToMediaItem} from '@app/web-player/tracks/utils/track-to-media-item';
import {apiClient} from '@common/http/query-client';
import type {PlayerState, PlayerStoreApi} from '@common/player/state/player-state';
import {
  canUseNativeAudioProvider,
  canUseNativeAudioPlaybackForMedia,
} from '@common/player/utils/can-use-native-audio-provider';

export type PlayerActions = ReturnType<typeof usePlayerActions>;

export function usePlayerActions() {
  const store = useContext(PlayerStoreContext);

  return useMemo(() => {
    const s = store.getState();
    const controlsLocked = () => store.getState().controlsLocked;

    const overrideQueueAndPlay = async (
      mediaItems: MediaItem[],
      queuePointer?: number
    ) => {
      if (controlsLocked()) {
        return;
      }

      const previewRestriction = getPlaybackPreviewRestriction();
      const hasPreviewRestriction = previewRestriction != null;
      const hasZeroSecondPreviewRestriction = previewRestriction === 0;
      const autoMixPreparation = prepareAutoMixAlbumQueue(
        mediaItems,
        queuePointer,
      );
      const queueToPlay = autoMixPreparation?.queue ?? mediaItems;
      const queuePointerToPlay = autoMixPreparation?.queuePointer ?? queuePointer;
      const state = store.getState();
      const targetIndex =
        typeof queuePointerToPlay === 'number' && queuePointerToPlay >= 0
          ? queuePointerToPlay
          : 0;

      if (
        canUseNativeAudioProvider() &&
        state.providerApi?.replaceQueue &&
        queueToPlay.every(item => canUseNativeAudioPlaybackForMedia(item))
      ) {
        if (queueToPlay.length) {
          await state.providerApi?.replaceQueue?.(
            queueToPlay,
            targetIndex,
            !hasZeroSecondPreviewRestriction,
          );
        }

        if (
          autoMixPreparation &&
          autoMixPreparation.recommendationSlots > 0
        ) {
          void hydrateAutoMixRecommendations(store, autoMixPreparation);
        }
        return;
      }

      s.stop(false);
      await s.overrideQueue(queueToPlay, queuePointerToPlay);

      if (
        autoMixPreparation &&
        autoMixPreparation.recommendationSlots > 0
      ) {
        void hydrateAutoMixRecommendations(store, autoMixPreparation);
      }

      return s.play();
    };

    return {
      play: async (...args: Parameters<typeof s.play>) => {
        if (controlsLocked()) {
          return;
        }
        return s.play(...args);
      },
      playNext: (...args: Parameters<typeof s.playNext>) => {
        if (controlsLocked()) {
          return;
        }
        return s.playNext(...args);
      },
      playPrevious: (...args: Parameters<typeof s.playPrevious>) => {
        if (controlsLocked()) {
          return;
        }
        return s.playPrevious(...args);
      },
      playQueueItemAtIndex: async (
        ...args: Parameters<typeof s.playQueueItemAtIndex>
      ) => {
        if (controlsLocked()) {
          return;
        }
        return s.playQueueItemAtIndex(...args);
      },
      pause: (...args: Parameters<typeof s.pause>) => {
        if (controlsLocked()) {
          return;
        }
        return s.pause(...args);
      },
      subscribe: s.subscribe,
      emit: s.emit,
      getCurrentTime: s.getCurrentTime,
      seek: (...args: Parameters<typeof s.seek>) => {
        if (controlsLocked()) {
          return;
        }
        return s.seek(...args);
      },
      toggleRepeatMode: (...args: Parameters<typeof s.toggleRepeatMode>) => {
        if (controlsLocked()) {
          return;
        }
        return s.toggleRepeatMode(...args);
      },
      toggleShuffling: (...args: Parameters<typeof s.toggleShuffling>) => {
        if (controlsLocked()) {
          return;
        }
        return s.toggleShuffling(...args);
      },
      getState: store.getState,
      setVolume: (...args: Parameters<typeof s.setVolume>) => {
        if (controlsLocked()) {
          return;
        }
        return s.setVolume(...args);
      },
      setMuted: (...args: Parameters<typeof s.setMuted>) => {
        if (controlsLocked()) {
          return;
        }
        return s.setMuted(...args);
      },
      appendToQueue: (...args: Parameters<typeof s.appendToQueue>) => {
        if (controlsLocked()) {
          return;
        }
        return s.appendToQueue(...args);
      },
      removeFromQueue: (...args: Parameters<typeof s.removeFromQueue>) => {
        if (controlsLocked()) {
          return;
        }
        return s.removeFromQueue(...args);
      },
      enterFullscreen: (...args: Parameters<typeof s.enterFullscreen>) => {
        if (controlsLocked()) {
          return;
        }
        return s.enterFullscreen(...args);
      },
      exitFullscreen: (...args: Parameters<typeof s.exitFullscreen>) => {
        if (controlsLocked()) {
          return;
        }
        return s.exitFullscreen(...args);
      },
      toggleFullscreen: (...args: Parameters<typeof s.toggleFullscreen>) => {
        if (controlsLocked()) {
          return;
        }
        return s.toggleFullscreen(...args);
      },
      enterPip: (...args: Parameters<typeof s.enterPip>) => {
        if (controlsLocked()) {
          return;
        }
        return s.enterPip(...args);
      },
      exitPip: (...args: Parameters<typeof s.exitPip>) => {
        if (controlsLocked()) {
          return;
        }
        return s.exitPip(...args);
      },
      setTextTrackVisibility: (
        ...args: Parameters<typeof s.setTextTrackVisibility>
      ) => {
        if (controlsLocked()) {
          return;
        }
        return s.setTextTrackVisibility(...args);
      },
      setCurrentTextTrack: (
        ...args: Parameters<typeof s.setCurrentTextTrack>
      ) => {
        if (controlsLocked()) {
          return;
        }
        return s.setCurrentTextTrack(...args);
      },
      setCurrentAudioTrack: (
        ...args: Parameters<typeof s.setCurrentAudioTrack>
      ) => {
        if (controlsLocked()) {
          return;
        }
        return s.setCurrentAudioTrack(...args);
      },
      setIsSeeking: (...args: Parameters<typeof s.setIsSeeking>) => {
        if (controlsLocked()) {
          return;
        }
        return s.setIsSeeking(...args);
      },
      setControlsVisible: s.setControlsVisible,
      cue: async (...args: Parameters<typeof s.cue>) => {
        if (controlsLocked()) {
          return;
        }
        return s.cue(...args);
      },
      overrideQueueAndPlay,
      overrideQueue: async (...args: Parameters<typeof s.overrideQueue>) => {
        if (controlsLocked()) {
          return;
        }
        return s.overrideQueue(...args);
      },
      setPlaybackRate: (...args: Parameters<typeof s.setPlaybackRate>) => {
        if (controlsLocked()) {
          return;
        }
        return s.setPlaybackRate(...args);
      },
      setPlaybackQuality: (...args: Parameters<typeof s.setPlaybackQuality>) => {
        if (controlsLocked()) {
          return;
        }
        return s.setPlaybackQuality(...args);
      },
    };
  }, [store]);
}

function getPlaybackPreviewRestriction(): number | null {
  const previewValue = getUserRolesAccess().userAccess.medias.play.preview;
  if (previewValue == null || previewValue === '' || typeof previewValue === 'boolean') {
    return null;
  }

  const normalized = Math.floor(Number(previewValue));
  return Number.isFinite(normalized) && normalized >= 0 ? normalized : null;
}

interface AutoMixQueueResult {
  queue: MediaItem[];
  queuePointer?: number;
}

interface AutoMixQueuePreparation extends AutoMixQueueResult {
  albumGroupId: string;
  seedTrackId: number;
  recommendationSlots: number;
  excludedIds: string[];
}

function prepareAutoMixAlbumQueue(
  queue: MediaItem[],
  queuePointer?: number,
): AutoMixQueuePreparation | null {
  if (queue.length <= 1) {
    return null;
  }

  const {userAccess} = getUserRolesAccess();
  const hasFullAlbumControl =
    userAccess.medias.play.base &&
    parseRestrictionBool(userAccess.medias.play.canQueueFullAlbum);
  const shouldAutoMixAlbum = !hasFullAlbumControl;
  if (!shouldAutoMixAlbum) {
    return null;
  }

  const albumGroupId = getAlbumQueueGroupId(queue);
  if (!albumGroupId) {
    return null;
  }

  const keepCount = Math.min(
    queue.length,
    Math.max(3, Math.round(queue.length * 0.1)),
  );
  const keptItems = getRandomItems(queue, keepCount);

  const recommendationSlots = Math.max(0, queue.length - keptItems.length);
  const seedTrackId = Number((keptItems[0]?.meta as Track | undefined)?.id);
  return {
    queue: keptItems,
    queuePointer: 0,
    albumGroupId,
    seedTrackId,
    recommendationSlots,
    excludedIds: keptItems.map(item => `${item.id}`),
  };
}

async function hydrateAutoMixRecommendations(
  store: PlayerStoreApi,
  preparation: AutoMixQueuePreparation,
) {
  if (
    preparation.recommendationSlots <= 0 ||
    !Number.isFinite(preparation.seedTrackId) ||
    preparation.seedTrackId <= 0
  ) {
    return;
  }

  const recommendations = await fetchTrackRecommendations(preparation.seedTrackId);
  if (!recommendations.length) {
    return;
  }

  const state = store.getState();
  if (!canHydrateAutoMixRecommendations(state, preparation)) {
    return;
  }

  const existingQueueIds = new Set(state.shuffledQueue.map(item => `${item.id}`));
  const recommendationItems = recommendations
    .filter(track => {
      const trackId = `${track.id}`;
      return (
        !preparation.excludedIds.includes(trackId) &&
        !existingQueueIds.has(trackId)
      );
    })
    .slice(0, preparation.recommendationSlots)
    .map(track => trackToMediaItem(track));

  if (!recommendationItems.length) {
    return;
  }

  state.addToQueue(recommendationItems, state.shuffledQueue.length - 1, false);
}

function canHydrateAutoMixRecommendations(
  state: PlayerState,
  preparation: AutoMixQueuePreparation,
): boolean {
  if (state.cuedMedia?.groupId !== preparation.albumGroupId) {
    return false;
  }

  return state.shuffledQueue.some(
    item => item.groupId === preparation.albumGroupId,
  );
}

function getAlbumQueueGroupId(queue: MediaItem[]): string | null {
  const firstGroupId = queue[0]?.groupId;
  if (typeof firstGroupId !== 'string' || !firstGroupId.startsWith('album.')) {
    return null;
  }

  const sameGroup = queue.every(item => item.groupId === firstGroupId);
  return sameGroup ? firstGroupId : null;
}

async function fetchTrackRecommendations(seedTrackId: number): Promise<Track[]> {
  if (!Number.isFinite(seedTrackId) || seedTrackId <= 0) {
    return [];
  }

  try {
    const response = await apiClient.get<{recommendations?: Track[]}>(
      `radio/track/${seedTrackId}`,
      {
        params: {
          context: 'player_queue',
          limit: 10,
        },
      },
    );
    return response.data?.recommendations ?? [];
  } catch {
    return [];
  }
}

function parseRestrictionBool(value: unknown): boolean {
  if (value == null || value === '') {
    return true;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  if (typeof value === 'string') {
    const normalized = value.toLowerCase();
    return !['false', '0', 'off', 'no'].includes(normalized);
  }
  return Boolean(value);
}

function getRandomItems<T>(items: T[], count: number): T[] {
  if (count >= items.length) {
    return [...items];
  }

  const shuffled = [...items];
  for (let index = shuffled.length - 1; index > 0; index--) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[randomIndex]] = [
      shuffled[randomIndex],
      shuffled[index],
    ];
  }
  return shuffled.slice(0, count);
}
